import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";

function TrafficSpikePanel({ events: scopedEvents = [], onRefresh }) {
  const events = Array.isArray(scopedEvents) ? scopedEvents : [];
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedIp, setSelectedIp] = useState(null);
  const [viewMode, setViewMode] = useState("all");
  const loading = false;
  const error = "";

  const fetchEvents = async () => {
    if (typeof onRefresh === "function") {
      await onRefresh();
    }
  };

  const filteredEvents = useMemo(() => {
    if (viewMode === "alerts") {
      return events.filter(
        (event) =>
          event.severity === "High" ||
          event.severity === "Medium" ||
          event.event_type?.toLowerCase().includes("failed") ||
          event.event_type?.toLowerCase().includes("firewall") ||
          event.event_type?.toLowerCase().includes("listening")
      );
    }

    if (viewMode === "remote") {
      return events.filter((event) => event.remote_ip);
    }

    return events;
  }, [events, viewMode]);

  const buckets = useMemo(() => buildSpikeBuckets(filteredEvents), [filteredEvents]);

  const visibleEvents = useMemo(() => {
    if (selectedIp) {
      return filteredEvents.filter(
        (event) => event.remote_ip === selectedIp || event.local_ip === selectedIp
      );
    }

    if (selectedBucket) {
      return selectedBucket.events;
    }

    return filteredEvents.slice(0, 30);
  }, [filteredEvents, selectedBucket, selectedIp]);

  const maxCount = Math.max(...buckets.map((bucket) => bucket.total), 1);

  if (loading) {
    return (
      <section style={styles.panel}>
        <h2 style={styles.title}>Traffic Spike Intelligence</h2>
        <p style={styles.muted}>Loading spike data...</p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <div style={styles.pill}>Congo Market AI • Traffic Spike View</div>
          <h2 style={styles.title}>Traffic Spike Intelligence</h2>
          <p style={styles.subtitle}>
            Click a spike, event card, source IP, or destination IP to drill into
            exact traffic details collected by the Wireless Press Lockdown agents.
          </p>
        </div>

        <button style={styles.refreshButton} onClick={fetchEvents}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={styles.modeRow}>
        <button
          style={viewMode === "all" ? styles.modeButtonActive : styles.modeButton}
          onClick={() => {
            setViewMode("all");
            resetSelection();
          }}
        >
          All Events
        </button>

        <button
          style={viewMode === "alerts" ? styles.modeButtonActive : styles.modeButton}
          onClick={() => {
            setViewMode("alerts");
            resetSelection();
          }}
        >
          Alerts / Risk Events
        </button>

        <button
          style={viewMode === "remote" ? styles.modeButtonActive : styles.modeButton}
          onClick={() => {
            setViewMode("remote");
            resetSelection();
          }}
        >
          Remote IP Only
        </button>
      </div>

      <div style={styles.chartWrap}>
        {buckets.map((bucket) => {
          const height =
            bucket.total > 0 ? Math.max((bucket.total / maxCount) * 180, 18) : 8;

          const isSelected = selectedBucket?.label === bucket.label;

          return (
            <button
              key={bucket.label}
              style={{
                ...styles.barButton,
                ...(isSelected ? styles.selectedBarButton : {}),
              }}
              onClick={() => {
                setSelectedBucket(bucket);
                setSelectedIp(null);
                setSelectedEvent(bucket.events[0] || null);
              }}
              title={`${bucket.total} events`}
            >
              <div style={styles.barStack}>
                <div
                  style={{
                    ...styles.highBar,
                    height: `${getSeverityHeight(bucket.high, bucket.total, height)}px`,
                  }}
                ></div>

                <div
                  style={{
                    ...styles.mediumBar,
                    height: `${getSeverityHeight(bucket.medium, bucket.total, height)}px`,
                  }}
                ></div>

                <div
                  style={{
                    ...styles.lowBar,
                    height: `${getSeverityHeight(bucket.low, bucket.total, height)}px`,
                  }}
                ></div>
              </div>

              <strong style={styles.barCount}>{bucket.total}</strong>
              <span style={styles.barLabel}>{bucket.label}</span>
            </button>
          );
        })}
      </div>

      <div style={styles.legendRow}>
        <Legend color="#ef4444" label="High" />
        <Legend color="#f59e0b" label="Medium" />
        <Legend color="#22c55e" label="Low" />
      </div>

      <div style={styles.filterRow}>
        <div>
          <strong style={styles.filterTitle}>
            {selectedIp
              ? `Showing events for IP: ${selectedIp}`
              : selectedBucket
              ? `Showing spike window: ${selectedBucket.label}`
              : "Showing latest events"}
          </strong>
          <p style={styles.filterSub}>
            {visibleEvents.length} event(s) in current view. Click any event to expand full details.
          </p>
        </div>

        {(selectedBucket || selectedIp || selectedEvent) && (
          <button style={styles.clearButton} onClick={resetSelection}>
            Clear Selection
          </button>
        )}
      </div>

      <div style={styles.contentGrid}>
        <div style={styles.eventList}>
          {visibleEvents.length === 0 ? (
            <div style={styles.emptyState}>No events found for this selection.</div>
          ) : (
            visibleEvents.slice(0, 40).map((event) => {
              const endpoints = getEventEndpoints(event);

              return (
                <div
                  key={event.id}
                  style={{
                    ...styles.eventCard,
                    ...(selectedEvent?.id === event.id ? styles.selectedEventCard : {}),
                  }}
                  onClick={() => setSelectedEvent(event)}
                >
                  <div style={styles.eventTop}>
                    <span style={getSeverityBadge(event.severity)}>
                      {event.severity || "Low"}
                    </span>

                    <span style={styles.eventType}>
                      {event.event_type || "Network Event"}
                    </span>

                    <span style={styles.clickHint}>Click for details</span>
                  </div>

                  <div style={styles.ipGrid}>
                    <div>
                      <span style={styles.ipLabel}>Source / Remote</span>
                      {isClickableIp(endpoints.sourceIp) ? (
                        <button
                          style={styles.ipButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIp(endpoints.sourceIp);
                            setSelectedBucket(null);
                            setSelectedEvent(event);
                          }}
                        >
                          {endpoints.sourceDisplay}
                        </button>
                      ) : (
                        <strong style={styles.ipText}>{endpoints.sourceDisplay}</strong>
                      )}
                    </div>

                    <div>
                      <span style={styles.ipLabel}>Destination / Local</span>
                      {isClickableIp(endpoints.destinationIp) ? (
                        <button
                          style={styles.ipButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIp(endpoints.destinationIp);
                            setSelectedBucket(null);
                            setSelectedEvent(event);
                          }}
                        >
                          {endpoints.destinationDisplay}
                        </button>
                      ) : (
                        <strong style={styles.ipText}>{endpoints.destinationDisplay}</strong>
                      )}
                    </div>
                  </div>

                  <p style={styles.eventMessage}>
                    {event.message || "No message available."}
                  </p>

                  <div style={styles.eventMeta}>
                    <span>Protocol: {event.protocol || "N/A"}</span>
                    <span>Port: {endpoints.destinationPort || endpoints.sourcePort || "N/A"}</span>
                    <span>Process: {event.process_name || "N/A"}</span>
                    <span>Status: {event.status || "N/A"}</span>
                    <span>{formatDate(event.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={styles.detailPanel}>
          {selectedEvent ? (
            <EventDetails
              event={selectedEvent}
              events={filteredEvents}
              setSelectedIp={setSelectedIp}
            />
          ) : (
            <div style={styles.emptyDetail}>
              <h3>Event Detail</h3>
              <p>
                Click an event card to view source, destination, protocol, process,
                status, timestamp, message, and related events.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  function resetSelection() {
    setSelectedBucket(null);
    setSelectedIp(null);
    setSelectedEvent(null);
  }
}

function EventDetails({ event, events, setSelectedIp }) {
  const endpoints = getEventEndpoints(event);

  const relatedEvents = events
    .filter((item) => {
      if (item.id === event.id) return false;

      return (
        item.remote_ip === event.remote_ip ||
        item.local_ip === event.local_ip ||
        item.remote_ip === event.local_ip ||
        item.local_ip === event.remote_ip
      );
    })
    .slice(0, 8);

  return (
    <div>
      <div style={styles.detailTop}>
        <span style={getSeverityBadge(event.severity)}>
          {event.severity || "Low"}
        </span>
        <strong>{event.event_type || "Network Event"}</strong>
      </div>

      <h3 style={styles.detailTitle}>Full Event Details</h3>

      <div style={styles.detailGrid}>
        <Detail label="Device" value={event.hostname} />
        <Detail
          label="Source IP"
          value={endpoints.sourceIp || "N/A"}
          onClick={
            isClickableIp(endpoints.sourceIp)
              ? () => setSelectedIp(endpoints.sourceIp)
              : null
          }
        />
        <Detail label="Source Port" value={endpoints.sourcePort || "N/A"} />
        <Detail
          label="Destination IP"
          value={endpoints.destinationIp || "N/A"}
          onClick={
            isClickableIp(endpoints.destinationIp)
              ? () => setSelectedIp(endpoints.destinationIp)
              : null
          }
        />
        <Detail label="Destination Port" value={endpoints.destinationPort || "N/A"} />
        <Detail label="Protocol" value={event.protocol || "N/A"} />
        <Detail label="Process" value={event.process_name || "N/A"} />
        <Detail label="Status" value={event.status || "N/A"} />
        <Detail label="Event ID" value={event.id || "N/A"} />
        <Detail label="Time" value={formatDate(event.created_at)} />
      </div>

      <div style={styles.messageBox}>
        <strong>Message</strong>
        <p>{event.message || "No message available."}</p>
      </div>

      <div style={styles.rawBox}>
        <strong>Raw Metadata</strong>
        <pre style={styles.rawPre}>{JSON.stringify(event, null, 2)}</pre>
      </div>

      <div style={styles.relatedBox}>
        <strong>Related Events</strong>

        {relatedEvents.length === 0 ? (
          <p style={styles.muted}>No related events found.</p>
        ) : (
          relatedEvents.map((item) => {
            const itemEndpoints = getEventEndpoints(item);

            return (
              <div key={item.id} style={styles.relatedItem}>
                <span style={getSeverityDot(item.severity)}></span>
                <div>
                  <strong>{item.event_type || "Network Event"}</strong>
                  <p>
                    {itemEndpoints.sourceDisplay} → {itemEndpoints.destinationDisplay} •{" "}
                    {item.protocol || "N/A"} • {formatDate(item.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Detail({ label, value, onClick }) {
  return (
    <div style={styles.detailItem}>
      <span style={styles.detailItemLabel}>{label}</span>

      {onClick ? (
        <button style={styles.detailButton} onClick={onClick}>
          {value || "N/A"}
        </button>
      ) : (
        <strong style={styles.detailValue}>{value || "N/A"}</strong>
      )}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div style={styles.legendItem}>
      <span style={{ ...styles.legendDot, background: color }}></span>
      {label}
    </div>
  );
}

function getEventEndpoints(event) {
  const sourceIp = event.remote_ip || "";
  const sourcePort = event.remote_port || "";

  const destinationIp = event.local_ip || "";
  const destinationPort = event.local_port || "";

  const sourceDisplay =
    sourceIp && sourceIp !== "null" ? `${sourceIp}${sourcePort ? `:${sourcePort}` : ""}` : "Local / Listening";

  const destinationDisplay =
    destinationIp && destinationIp !== "null"
      ? `${destinationIp}${destinationPort ? `:${destinationPort}` : ""}`
      : "N/A";

  return {
    sourceIp,
    sourcePort,
    destinationIp,
    destinationPort,
    sourceDisplay,
    destinationDisplay,
  };
}

function isClickableIp(ip) {
  if (!ip) return false;

  const blocked = ["N/A", "Local / Listening", "0.0.0.0", "::", "127.0.0.1", "::1"];
  return !blocked.includes(ip);
}

function buildSpikeBuckets(events) {
  const bucketCount = 6;
  const recentEvents = Array.isArray(events) ? events.slice(0, 120).reverse() : [];

  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    label: index === bucketCount - 1 ? "Now" : `${(bucketCount - index) * 10}m`,
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
    events: [],
  }));

  if (recentEvents.length === 0) {
    return buckets;
  }

  const eventsPerBucket = Math.max(Math.ceil(recentEvents.length / bucketCount), 1);

  recentEvents.forEach((event, index) => {
    const bucketIndex = Math.min(
      Math.floor(index / eventsPerBucket),
      bucketCount - 1
    );

    const bucket = buckets[bucketIndex];

    bucket.total += 1;
    bucket.events.push(event);

    if (event.severity === "High") {
      bucket.high += 1;
    } else if (event.severity === "Medium") {
      bucket.medium += 1;
    } else {
      bucket.low += 1;
    }
  });

  return buckets;
}

function getSeverityHeight(count, total, totalHeight) {
  if (!total || !count) return 0;
  return Math.max((count / total) * totalHeight, 4);
}

function getSeverityBadge(severity) {
  if (severity === "High") {
    return {
      ...styles.badge,
      background: "#fee2e2",
      color: "#991b1b",
    };
  }

  if (severity === "Medium") {
    return {
      ...styles.badge,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    ...styles.badge,
    background: "#dcfce7",
    color: "#166534",
  };
}

function getSeverityDot(severity) {
  if (severity === "High") return { ...styles.smallDot, background: "#ef4444" };
  if (severity === "Medium") return { ...styles.smallDot, background: "#f59e0b" };
  return { ...styles.smallDot, background: "#22c55e" };
}

function formatDate(value) {
  if (!value) return "N/A";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    padding: "22px",
    marginBottom: "18px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "18px",
    alignItems: "flex-start",
    marginBottom: "18px",
  },
  pill: {
    display: "inline-flex",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "#e0f2fe",
    color: "#075985",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "10px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    letterSpacing: "-0.04em",
    color: "#0f172a",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    maxWidth: "840px",
  },
  muted: {
    color: "#64748b",
  },
  refreshButton: {
    border: "0",
    background: "#0f172a",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "14px",
    fontWeight: "900",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  modeRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "14px",
  },
  modeButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "10px 12px",
    borderRadius: "999px",
    fontWeight: "800",
    cursor: "pointer",
  },
  modeButtonActive: {
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "10px 12px",
    borderRadius: "999px",
    fontWeight: "900",
    cursor: "pointer",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "14px",
    padding: "12px 14px",
    marginBottom: "16px",
  },
  chartWrap: {
    minHeight: "235px",
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "12px",
    alignItems: "end",
    padding: "16px",
    borderRadius: "18px",
    background:
      "linear-gradient(#f1f5f9 1px, transparent 1px), linear-gradient(90deg, #f1f5f9 1px, transparent 1px)",
    backgroundSize: "36px 36px",
    border: "1px solid #e2e8f0",
  },
  barButton: {
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    background: "rgba(255,255,255,0.9)",
    minHeight: "215px",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    cursor: "pointer",
  },
  selectedBarButton: {
    border: "2px solid #2563eb",
    boxShadow: "0 12px 24px rgba(37, 99, 235, 0.16)",
  },
  barStack: {
    width: "38px",
    minHeight: "10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    borderRadius: "999px",
    overflow: "hidden",
    background: "#e2e8f0",
  },
  highBar: {
    width: "100%",
    background: "#ef4444",
  },
  mediumBar: {
    width: "100%",
    background: "#f59e0b",
  },
  lowBar: {
    width: "100%",
    background: "#22c55e",
  },
  barCount: {
    color: "#0f172a",
    fontSize: "18px",
  },
  barLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "800",
  },
  legendRow: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    margin: "12px 0 18px",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "800",
  },
  legendDot: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
  },
  filterRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    marginBottom: "16px",
  },
  filterTitle: {
    color: "#0f172a",
  },
  filterSub: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },
  clearButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "10px 12px",
    borderRadius: "12px",
    fontWeight: "800",
    cursor: "pointer",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.15fr) minmax(360px, 0.85fr)",
    gap: "16px",
  },
  eventList: {
    display: "grid",
    gap: "12px",
    maxHeight: "720px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  eventCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "14px",
    background: "#f8fafc",
    cursor: "pointer",
  },
  selectedEventCard: {
    border: "2px solid #2563eb",
    background: "#eff6ff",
  },
  eventTop: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
    flexWrap: "wrap",
  },
  badge: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "900",
  },
  eventType: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "800",
  },
  clickHint: {
    marginLeft: "auto",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: "900",
  },
  ipGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "10px",
  },
  ipLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: "900",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  ipButton: {
    border: "0",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    fontWeight: "900",
    cursor: "pointer",
    textAlign: "left",
    wordBreak: "break-word",
  },
  ipText: {
    color: "#0f172a",
    wordBreak: "break-word",
  },
  eventMessage: {
    margin: "0 0 10px",
    color: "#475569",
    lineHeight: 1.5,
  },
  eventMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "700",
  },
  detailPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    padding: "16px",
    background: "#ffffff",
    minHeight: "420px",
    maxHeight: "720px",
    overflowY: "auto",
  },
  emptyDetail: {
    color: "#64748b",
    lineHeight: 1.6,
  },
  detailTop: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },
  detailTitle: {
    margin: "0 0 12px",
    color: "#0f172a",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  detailItem: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "10px",
    minWidth: 0,
  },
  detailItemLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: "900",
    textTransform: "uppercase",
    marginBottom: "5px",
  },
  detailValue: {
    display: "block",
    color: "#0f172a",
    wordBreak: "break-word",
    lineHeight: 1.35,
  },
  detailButton: {
    border: "0",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    fontWeight: "900",
    cursor: "pointer",
    wordBreak: "break-word",
    textAlign: "left",
    lineHeight: 1.35,
  },
  messageBox: {
    marginTop: "14px",
    padding: "12px",
    borderRadius: "14px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
  },
  rawBox: {
    marginTop: "14px",
    padding: "12px",
    borderRadius: "14px",
    background: "#020617",
    color: "#e2e8f0",
    overflowX: "auto",
  },
  rawPre: {
    margin: "10px 0 0",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  relatedBox: {
    marginTop: "14px",
  },
  relatedItem: {
    display: "grid",
    gridTemplateColumns: "10px 1fr",
    gap: "10px",
    padding: "10px 0",
    borderBottom: "1px solid #e2e8f0",
  },
  smallDot: {
    width: "9px",
    height: "9px",
    borderRadius: "999px",
    marginTop: "5px",
  },
  emptyState: {
    padding: "18px",
    borderRadius: "18px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    textAlign: "center",
  },
};

export default TrafficSpikePanel;